// Color utilities for the Stonks heatmap system

// ── Market heatmap colors (based on % change) ────────────────────────────────
// Returns an HSL color string. Matches TradingView's palette.
export function getMarketColor(pct) {
  if (pct == null || isNaN(pct)) return '#363A45';
  const abs = Math.abs(pct);
  if (pct >= 0) {
    // greens: dim green at 0 → bright emerald at ≥3%
    const intensity = Math.min(abs / 3, 1);
    const lightness = Math.round(22 + intensity * 20); // 22% → 42%
    const saturation = Math.round(50 + intensity * 30); // 50% → 80%
    return `hsl(160, ${saturation}%, ${lightness}%)`;
  } else {
    // reds: dim red at 0 → deep crimson at ≤-3%
    const intensity = Math.min(abs / 3, 1);
    const lightness = Math.round(18 + intensity * 18); // 18% → 36%
    const saturation = Math.round(60 + intensity * 30); // 60% → 90%
    return `hsl(4, ${saturation}%, ${lightness}%)`;
  }
}

// Text color that's legible on a given heatmap tile
export function getTileTextColor(pct) {
  if (pct == null) return '#d1d4dc';
  const abs = Math.abs(pct);
  if (abs < 0.3) return '#9da5b4'; // near-neutral tiles get muted text
  return pct >= 0 ? '#b7ead4' : '#f8c0c5';
}

// ── Transaction heatmap colors (based on type + amount) ──────────────────────
// intensity: 0 = lightest, 1 = darkest
export function getTransactionColor(type, intensity = 0.5) {
  const clamped = Math.max(0, Math.min(1, intensity));
  const isBuy = type === 'purchase' || type === 'buy';
  if (isBuy) {
    const lightness = Math.round(62 - clamped * 38); // 62% → 24%
    const saturation = Math.round(55 + clamped * 25); // 55% → 80%
    return `hsl(145, ${saturation}%, ${lightness}%)`;
  } else {
    const lightness = Math.round(58 - clamped * 36); // 58% → 22%
    const saturation = Math.round(65 + clamped * 25); // 65% → 90%
    return `hsl(4, ${saturation}%, ${lightness}%)`;
  }
}

// Generate CSS gradient string for multiple transaction stripes.
// Each transaction gets an equal-height band (1/n of tile height).
// Color encodes buy/sell + intensity (relative amount).
// Sorted newest-first so the top stripe is the most recent transaction.
export function buildTransactionGradient(stripes) {
  if (!stripes || stripes.length === 0) return '#363A45';
  if (stripes.length === 1) {
    return getTransactionColor(stripes[0].type, stripes[0].intensity);
  }

  const n = stripes.length;
  const stops = [];
  stripes.forEach((stripe, i) => {
    const pct0 = ((i / n) * 100).toFixed(3);
    const pct1 = (((i + 1) / n) * 100).toFixed(3);
    const color = getTransactionColor(stripe.type, stripe.intensity);
    stops.push(`${color} ${pct0}%`);
    stops.push(`${color} ${pct1}%`);
  });
  return `linear-gradient(to bottom, ${stops.join(', ')})`;
}

// ── Amount parsing ────────────────────────────────────────────────────────────
// Parses ranges like "$1,000,001 - $5,000,000" → midpoint 3,000,000
export function parseAmount(amountStr) {
  if (!amountStr) return 0;
  if (typeof amountStr === 'number') return amountStr;
  const cleaned = String(amountStr).replace(/[$,]/g, '').trim();
  if (cleaned.includes(' - ')) {
    const parts = cleaned.split(' - ').map(s => parseFloat(s.replace(/[^0-9.]/g, '')));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return (parts[0] + parts[1]) / 2;
    }
  }
  const num = parseFloat(cleaned.replace(/[^0-9.]/g, ''));
  return isNaN(num) ? 0 : num;
}

// Normalize amounts to [0,1] range given a min and max
export function normalizeAmount(amount, min, max) {
  if (max <= min) return 0.5;
  return Math.max(0, Math.min(1, (amount - min) / (max - min)));
}

// ── Formatting helpers ────────────────────────────────────────────────────────
export function fmtPct(val, decimals = 2) {
  if (val == null) return '—';
  const s = (val * 1).toFixed(decimals);
  return (val >= 0 ? '+' : '') + s + '%';
}

export function fmtMktCap(val) {
  if (!val) return '—';
  if (val >= 1e12) return '$' + (val / 1e12).toFixed(2) + 'T';
  if (val >= 1e9)  return '$' + (val / 1e9).toFixed(2) + 'B';
  if (val >= 1e6)  return '$' + (val / 1e6).toFixed(2) + 'M';
  return '$' + val.toLocaleString();
}

export function fmtNum(val, decimals = 2) {
  if (val == null) return '—';
  return parseFloat(val).toFixed(decimals);
}

export function fmtPrice(val) {
  if (val == null) return '—';
  return '$' + parseFloat(val).toFixed(2);
}

export function fmtLargeNum(val) {
  if (!val) return '—';
  if (val >= 1e12) return (val / 1e12).toFixed(1) + 'T';
  if (val >= 1e9)  return (val / 1e9).toFixed(1) + 'B';
  if (val >= 1e6)  return (val / 1e6).toFixed(1) + 'M';
  return val.toLocaleString();
}

// Appropriate font size for a heatmap tile
export function getTileFontSize(width, height) {
  const area = width * height;
  if (area < 900) return 0;    // too small, hide text
  if (area < 2500) return 8;
  if (area < 6000) return 9;
  if (area < 14000) return 10;
  if (area < 30000) return 11;
  return Math.min(16, Math.round(Math.sqrt(area) / 18));
}
